import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Phase 4.0 — AITrackFeedback
 * Small card showing AI-generated quality scores for a track.
 * Lazy-loads on mount: first call may take a few seconds (AI generation);
 * subsequent visits are cached server-side.
 */
const Bar = ({ label, value }) => (
  <div className="flex items-center gap-3">
    <span className="w-24 text-xs text-gray-400 shrink-0">{label}</span>
    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 transition-all"
        style={{ width: `${value}%` }}
      />
    </div>
    <span className="w-10 text-right text-xs font-semibold text-white tabular-nums">{value}</span>
  </div>
);

export const AITrackFeedback = ({ submissionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!submissionId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/track/${submissionId}/ai-feedback${refresh ? "?refresh=true" : ""}`);
      setFeedback(r.data.feedback);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // Phase 18.0 — Graceful AI degradation. 502 / "AI service unavailable"
      // / missing EMERGENT_LLM_KEY → friendly amber message; 403 → owner-
      // only notice; anything else falls through to a regular retry block.
      if (status === 502 || (typeof detail === "string" && /AI service unavailable|EMERGENT_LLM_KEY/i.test(detail))) {
        setError("ai_offline");
      } else if (status === 403) {
        setError("forbidden");
      } else {
        setError(typeof detail === "string" ? detail : "Couldn't load AI feedback");
      }
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { load(false); }, [load]);

  return (
    <div
      className="mt-6 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-black/40 p-5"
      data-testid="ai-feedback-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">🤖</span>
          <h3 className="text-white font-bold text-sm tracking-wide">AI Track Feedback</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">Beta</span>
        </div>
        {!loading && feedback && (
          <button
            type="button"
            onClick={() => load(true)}
            className="text-xs text-purple-300 hover:text-white transition-colors"
            data-testid="ai-feedback-refresh"
          >
            Regenerate
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-4" data-testid="ai-feedback-loading">
          <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-400">Analyzing your track…</span>
        </div>
      )}

      {error && !loading && (
        <div className="py-3" data-testid="ai-feedback-error">
          {error === "ai_offline" ? (
            <>
              <p className="text-sm text-amber-200">
                🛠 AI track feedback is temporarily offline. We&apos;re working
                on it — your track and stats are unaffected.
              </p>
              <button
                type="button"
                onClick={() => load(false)}
                className="mt-2 text-xs text-purple-300 hover:text-white"
                data-testid="ai-feedback-retry-offline"
              >
                Try again
              </button>
            </>
          ) : error === "forbidden" ? (
            <p className="text-sm text-gray-400">
              AI feedback is private — only the track owner and RGM staff can view it.
            </p>
          ) : (
            <>
              <p className="text-sm text-red-300">{error}</p>
              <button
                type="button"
                onClick={() => load(false)}
                className="mt-2 text-xs text-purple-300 hover:text-white"
              >
                Try again
              </button>
            </>
          )}
        </div>
      )}

      {!loading && !error && feedback && (
        <div className="space-y-2.5" data-testid="ai-feedback-content">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-xs">Overall</span>
            <span
              className={`text-2xl font-black tabular-nums ${
                feedback.overall >= 80 ? "text-green-400" : feedback.overall >= 60 ? "text-yellow-300" : "text-orange-300"
              }`}
              data-testid="ai-feedback-overall"
            >
              {feedback.overall}
              <span className="text-xs text-gray-500 ml-1">/ 100</span>
            </span>
          </div>
          <Bar label="Energy" value={feedback.energy} />
          <Bar label="Mix Clarity" value={feedback.mix_clarity} />
          <Bar label="Hook Strength" value={feedback.hook_strength} />
          {feedback.summary && (
            <p className="mt-3 text-sm text-gray-300 leading-relaxed italic">
              “{feedback.summary}”
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AITrackFeedback;
